import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Grid,
} from "@mui/material";
import SystemSettings from "src/service/SystemSettings";
import WEBSITES from "src/configs/websites";

const SiteSettings = ({ onDataChange }) => {
  const [enabledForSites, setEnabledForSites] = useState([]);
  const [commissionPercent, setCommissionPercent] = useState(1);
  const [minInRub, setMinInRub] = useState(1000);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await SystemSettings.getAutoChangeSettings();
      const data = response.data;

      if (data.enabledForSites) {
        setEnabledForSites(data.enabledForSites);
      } else {
        // Инициализируем с дефолтными значениями
        const defaultSites = WEBSITES.map((site) => ({
          siteId: site.id,
          enabled: false,
        }));
        setEnabledForSites(defaultSites);
      }

      if (data.commissionPercent !== undefined) {
        setCommissionPercent(data.commissionPercent);
      }

      if (data.minInRub !== undefined) {
        setMinInRub(data.minInRub);
      }
    } catch (err) {
      console.error("Ошибка загрузки настроек автообмена:", err);
      setError("Ошибка загрузки настроек");
    } finally {
      setLoading(false);
    }
  };

  const handleSiteToggle = (siteId) => {
    const updatedSites = enabledForSites.map((site) =>
      site.siteId === siteId ? { ...site, enabled: !site.enabled } : site
    );
    setEnabledForSites(updatedSites);

    // Уведомляем родительский компонент об изменении
    if (onDataChange) {
      onDataChange({
        enabledForSites: updatedSites,
        commissionPercent,
        minInRub,
      });
    }
  };

  const handleCommissionChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setCommissionPercent(value);

    // Уведомляем родительский компонент об изменении
    if (onDataChange) {
      onDataChange({ enabledForSites, commissionPercent: value, minInRub });
    }
  };

  const handleMinInRubChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setMinInRub(value);

    // Уведомляем родительский компонент об изменении
    if (onDataChange) {
      onDataChange({ enabledForSites, commissionPercent, minInRub: value });
    }
  };

  const getSiteName = (siteId) => {
    const site = WEBSITES.find((s) => s.id === siteId);
    return site ? site.name : `Сайт ${siteId}`;
  };

  const getSiteColor = (siteId) => {
    const site = WEBSITES.find((s) => s.id === siteId);
    return site ? site.color : "#2196f3";
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Настройки по сайтам"
        subheader="Включить/выключить автообмен для каждого сайта"
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Процент комиссии"
              type="number"
              value={commissionPercent}
              onChange={handleCommissionChange}
              inputProps={{
                min: 0,
                max: 100,
                step: 0.01,
              }}
              helperText="Процент комиссии для автообмена (0-100),прибавляется ко всем остальным комиссиям (сервис 0.5 + за перевод)"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Минимальная сумма в рублях"
              type="number"
              value={minInRub}
              onChange={handleMinInRubChange}
              inputProps={{
                min: 0,
                step: 1,
              }}
              helperText="Минимальная сумма для автообмена в рублях"
              variant="outlined"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          {enabledForSites.map((site) => (
            <Box
              key={site.siteId}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1,
                px: 2,
                mb: 1,
                borderRadius: 1,
                backgroundColor: "grey.50",
                border: `1px solid ${getSiteColor(site.siteId)}20`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: getSiteColor(site.siteId),
                    mr: 2,
                  }}
                />
                <Typography variant="body1" fontWeight="medium">
                  {getSiteName(site.siteId)}
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={site.enabled}
                    onChange={() => handleSiteToggle(site.siteId)}
                    color="primary"
                  />
                }
                label={site.enabled ? "Включен" : "Выключен"}
                labelPlacement="start"
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SiteSettings;
